import React, { FC, useEffect, useRef, useState } from 'react'
import {
  Container,
  QuadrantsContainer,
  AbsoluteContainer,
  QuadrantTagContainer,
  QuadrantTag,
  BodyQuadrant,
  ShowName,
  QuadrantCard,
  QuadrantHeader,
  QuadrantInfo,
  QuadrantName,
  QuadrantDescription,
  QuadrantStats,
  QuadrantBadge,
  QuadrantMeta,
  EmptyStateCard,
  EmptyStateTitle,
  EmptyStateHint,
  ScrollCue,
  ScrollCueText,
  ScrollCueChevrons,
} from './styled'
import { TeamMapProps } from 'views/containers/TeamMap'

type QuadrantCode = 'FI' | 'FD' | 'BI' | 'BD'
type ScrollHintState = Record<QuadrantCode, { hasOverflow: boolean; atBottom: boolean }>

const QUADRANT_COPY: Record<QuadrantCode, { title: string; description: string }> = {
  FI: {
    title: 'Frontal izquierdo',
    description: 'Analisis, estructura y criterio tecnico',
  },
  FD: {
    title: 'Frontal derecho',
    description: 'Innovacion, estrategia y vision de cambio',
  },
  BI: {
    title: 'Basal izquierdo',
    description: 'Orden, continuidad y control operativo',
  },
  BD: {
    title: 'Basal derecho',
    description: 'Relacionamiento, servicio y cohesion',
  },
}

const getMetricLabel = (talents?: boolean, variant?: 'dominance' | 'talents' | 'plan') => {
  if (talents) {
    return 'Talento destacado'
  }

  if (variant === 'plan') {
    return 'Foco de desarrollo'
  }

  return 'Predominio'
}

const BrainMap: FC<TeamMapProps & { variant?: 'dominance' | 'talents' | 'plan' }> = ({ usersByQuadrant, talents, variant }) => {
  const [showFd, setShowFd] = useState(false)
  const [showFi, setShowFi] = useState(false)
  const [showBi, setShowBi] = useState(false)
  const [showBd, setShowBd] = useState(false)
  const bodyRefs = useRef<Record<QuadrantCode, HTMLDivElement | null>>({
    FI: null,
    FD: null,
    BI: null,
    BD: null,
  })
  const [scrollHints, setScrollHints] = useState<ScrollHintState>({
    FI: { hasOverflow: false, atBottom: false },
    FD: { hasOverflow: false, atBottom: false },
    BI: { hasOverflow: false, atBottom: false },
    BD: { hasOverflow: false, atBottom: false },
  })
  const totalUsers =
    (usersByQuadrant?.fi?.length || 0) +
    (usersByQuadrant?.fd?.length || 0) +
    (usersByQuadrant?.bi?.length || 0) +
    (usersByQuadrant?.bd?.length || 0)

  const buildMeta = (code: QuadrantCode, amount: number) => {
    const percentage = totalUsers > 0 ? Math.round((amount / totalUsers) * 100) : 0

    return {
      title: QUADRANT_COPY[code].title,
      description: QUADRANT_COPY[code].description,
      amount,
      percentage,
    }
  }

  const metricLabel = getMetricLabel(talents, variant)
  const shouldShowScrollIndicator = variant === 'dominance' || variant === 'talents'

  const updateScrollHint = (code: QuadrantCode) => {
    const node = bodyRefs.current[code]

    if (!node) {
      return
    }

    const hasOverflow = node.scrollHeight > node.clientHeight + 4
    const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 4

    setScrollHints((currentState) => {
      const nextState = { hasOverflow, atBottom }
      const previousState = currentState[code]

      if (
        previousState.hasOverflow === nextState.hasOverflow &&
        previousState.atBottom === nextState.atBottom
      ) {
        return currentState
      }

      return {
        ...currentState,
        [code]: nextState,
      }
    })
  }

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      ;(['FI', 'FD', 'BI', 'BD'] as QuadrantCode[]).forEach(updateScrollHint)
    }, 0)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [usersByQuadrant, showFi, showFd, showBi, showBd, talents, variant])

  const renderEmptyState = () => {
    if (talents) {
      return (
        <EmptyStateCard>
          <EmptyStateTitle>Sin talentos destacados en este cuadrante</EmptyStateTitle>
          <EmptyStateHint>Aún no hay evaluados con puntaje superior a 80 en esta dimensión.</EmptyStateHint>
        </EmptyStateCard>
      )
    }

    if (variant === 'dominance') {
      return (
        <EmptyStateCard>
          <EmptyStateTitle>Sin evaluados en este cuadrante</EmptyStateTitle>
          <EmptyStateHint>Ninguna persona del equipo tiene aquí su Dominancia Natural.</EmptyStateHint>
        </EmptyStateCard>
      )
    }

    return <ShowName>No hay evaluados en este cuadrante</ShowName>
  }

  const renderQuadrantCard = ({
    code,
    left,
    isOpen,
    onToggle,
    users,
    scoreKey,
  }: {
    code: QuadrantCode
    left?: boolean
    isOpen: boolean
    onToggle: () => void
    users?: any[]
    scoreKey: 'FI' | 'FD' | 'BI' | 'BD'
  }) => {
    const meta = buildMeta(code, users?.length || 0)
    const showScrollCue =
      shouldShowScrollIndicator &&
      isOpen &&
      (users?.length || 0) > 0 &&
      scrollHints[code].hasOverflow &&
      !scrollHints[code].atBottom

    return (
      <QuadrantCard>
        <QuadrantHeader left={left}>
          <QuadrantInfo>
            <QuadrantName>{meta.title}</QuadrantName>
            <QuadrantDescription>{meta.description}</QuadrantDescription>
          </QuadrantInfo>
          <QuadrantStats>
            <QuadrantBadge>{meta.amount} personas</QuadrantBadge>
            <QuadrantMeta>
              {metricLabel}: {meta.percentage}%
            </QuadrantMeta>
          </QuadrantStats>
        </QuadrantHeader>
        <QuadrantTagContainer alignITems="center">
          <QuadrantTag show={isOpen} onClick={onToggle}>
            {isOpen ? 'Ocultar' : 'Ver detalle'}
          </QuadrantTag>
        </QuadrantTagContainer>
        <BodyQuadrant
          left={left}
          ref={(node) => {
            bodyRefs.current[code] = node
          }}
          onScroll={() => updateScrollHint(code)}
        >
          {isOpen &&
            ((users?.length || 0) > 0 ? (
              users
                ?.slice()
                .sort((a: any, b: any) => Number(b[scoreKey]) - Number(a[scoreKey]))
                .map((user, index) =>
                  !talents ? (
                    <ShowName left={left} key={`${code.toLowerCase()}-${index}`}>
                      {user.full_name}
                    </ShowName>
                  ) : (
                    <ShowName left={left} key={`${code.toLowerCase()}-${index}`}>
                      {user.full_name} - {user[scoreKey]}
                    </ShowName>
                  ),
                )
            ) : (
              renderEmptyState()
            ))}
          {showScrollCue ? (
            <ScrollCue>
              <ScrollCueText>Desliza para ver más</ScrollCueText>
              <ScrollCueChevrons>
                <span>⌄</span>
                <span>⌄</span>
              </ScrollCueChevrons>
            </ScrollCue>
          ) : null}
        </BodyQuadrant>
      </QuadrantCard>
    )
  }

  return (
    <Container>
      <QuadrantsContainer>
        {renderQuadrantCard({
          code: 'FI',
          left: true,
          isOpen: showFi,
          onToggle: () => setShowFi(!showFi),
          users: usersByQuadrant?.fi,
          scoreKey: 'FI',
        })}
        {renderQuadrantCard({
          code: 'FD',
          isOpen: showFd,
          onToggle: () => setShowFd(!showFd),
          users: usersByQuadrant?.fd,
          scoreKey: 'FD',
        })}
        {renderQuadrantCard({
          code: 'BI',
          left: true,
          isOpen: showBi,
          onToggle: () => setShowBi(!showBi),
          users: usersByQuadrant?.bi,
          scoreKey: 'BI',
        })}
        {renderQuadrantCard({
          code: 'BD',
          isOpen: showBd,
          onToggle: () => setShowBd(!showBd),
          users: usersByQuadrant?.bd,
          scoreKey: 'BD',
        })}
      </QuadrantsContainer>
      <AbsoluteContainer />
    </Container>
  )
}

export default BrainMap
